import React, { useEffect, useMemo, useRef, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";

import {
  approvePost,
  regeneratePost,
  deletePost,
  editImageFn,
  regenImageFn,
  fixPostQuality,
  pendingRef,
} from "./blogFirebase";
import { useBlogTask } from "./useBlogTask";
import ConfirmModal   from "./ConfirmModal";
import BlogActionBar  from "./BlogActionBar";
import BlogDiffPanel  from "./BlogDiffPanel";
import BlogPostBody   from "./BlogPostBody";
import BlogImageModal from "./BlogImageModal";
import RefreshRationale from "./RefreshRationale";
import QualityReportPanel from "./QualityReportPanel";
import { usePriorVersion } from "./usePriorVersion";
import { resolveContentHtml } from "./postContent";

// ── AdminBlogPost ─────────────────────────────────────────────────────────────
const AdminBlogPost = () => {
  const { docId } = useParams();
  const navigate  = useNavigate();

  const [post, setPost]         = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [confirmModal, setConfirmModal] = useState(null);

  // Edit panel state
  const [showEdit, setShowEdit]           = useState(false);
  const [showRegenForm, setShowRegenForm] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState("");

  // Image modal state — null | "main" | "secondary"
  const [imageModal, setImageModal] = useState(null);

  // Main image form state (used inside modal)
  const [imgFormMode, setImgFormMode]         = useState("edit");
  const [editPrompt, setEditPrompt]           = useState("");
  const [newImgPrompt, setNewImgPrompt]       = useState("");
  const [newImgUseDefault, setNewImgUseDefault] = useState(true);

  // Secondary image form state (used inside modal)
  const [secondaryImgFormMode, setSecondaryImgFormMode]           = useState("edit");
  const [secondaryEditPrompt, setSecondaryEditPrompt]             = useState("");
  const [secondaryNewImgPrompt, setSecondaryNewImgPrompt]         = useState("");
  const [secondaryNewImgUseDefault, setSecondaryNewImgUseDefault] = useState(true);

  // Prompt reveal state (shown below images)
  const [showImgPrompt, setShowImgPrompt]                   = useState(false);
  const [showSecondaryImgPrompt, setShowSecondaryImgPrompt] = useState(false);

  // Version diff — resolves a refresh snapshot or a replacedPrior fetch.
  const { showDiff, priorPost, hasPrior, diffLoading, diffLabel, toggleDiff } =
    usePriorVersion(post);

  // The diff panel mounts near the top of the document while the button that
  // opens it lives in a sticky bar that follows the reader down the page.
  // Without this, clicking "View changes" from inside the article inserted the
  // panel far above the viewport and nothing appeared to happen. Scroll owned
  // here rather than in usePriorVersion so that hook stays free of DOM concerns.
  const diffRef = useRef(null);
  useEffect(() => {
    if (!showDiff || !priorPost || !diffRef.current) return;
    const STICKY_BAR_OFFSET = 72;
    const top =
      diffRef.current.getBoundingClientRect().top +
      window.scrollY -
      STICKY_BAR_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
  }, [showDiff, priorPost]);

  // Live task progress (Firestore WebSocket listener)
  const { taskMsg, startTask, stopTask } = useBlogTask();

  // Reset busy when navigating to a different doc
  useEffect(() => { setBusy(false); }, [docId]);

  // Subscribe to this specific pending doc
  useEffect(() => {
    const ref  = pendingRef(docId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setNotFound(true);
        setPost(null);
      } else {
        setPost({ id: snap.id, ...snap.data() });
        setNotFound(false);
      }
    }, (err) => {
      console.error("BlogPost snapshot error:", err);
      setNotFound(true);
    });
    return () => unsub();
  }, [docId]);

  const setMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(""), 5000);
  };

  const contentHtml = useMemo(() => resolveContentHtml(post), [post]);

  // ── Confirm modal helpers ─────────────────────────────────────────────────
  const openConfirm  = (opts) => setConfirmModal(opts);
  const closeConfirm = () => setConfirmModal(null);

  // ── Action handlers ───────────────────────────────────────────────────────
  const handleApprove = () => {
    openConfirm({
      title: "Approve & Publish",
      message: `"${post.title}" will be published to the live blog.`,
      confirmLabel: "✅ Publish",
      confirmClass: "bg-green-600 hover:bg-green-700",
      onConfirm: doApprove,
    });
  };

  const doApprove = async () => {
    closeConfirm();
    setActionMsg("✅ Publishing post…");
    setBusy(true);
    try {
      await approvePost({ docId });
      navigate("/blog");
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    }
  };

  const handleDelete = () => {
    openConfirm({
      title: "Delete Post",
      message: `"${post.title}" will be permanently deleted. This cannot be undone.`,
      confirmLabel: "🗑 Delete",
      confirmClass: "bg-red-600 hover:bg-red-700",
      onConfirm: doDelete,
    });
  };

  const doDelete = async () => {
    closeConfirm();
    setBusy(true);
    try {
      await deletePost({ docId });
      navigate("/blog");
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    setActionMsg("✏️ Rewriting post with AI…");
    setBusy(true);
    setShowRegenForm(false);
    setShowEdit(false);
    const taskId = startTask();
    try {
      const result = await regeneratePost({ docId, feedbackNote: regenFeedback.trim(), taskId });
      setRegenFeedback("");
      navigate(`/blog/${result.data.newDocId}`);
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    } finally {
      stopTask();
    }
  };

  // Re-runs the backend quality pipeline over this post and updates it in place.
  // No navigation and no refetch: the doc keeps its ID, so the onSnapshot
  // listener above re-renders the panel with the new report on its own.
  const handleFixQuality = async () => {
    setActionMsg("🔧 Fixing quality issues…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      const { data } = await fixPostQuality({ docId, taskId });
      if (!data.changed) {
        setMsg("ℹ️ Nothing could be fixed automatically — the remaining issues need a manual edit.");
      } else {
        const fixed = Array.isArray(data.fixedCodes) ? data.fixedCodes.length : 0;
        setMsg(
          `🔧 Fixed ${fixed} issue${fixed === 1 ? "" : "s"} — ` +
          `${data.errorCount} error${data.errorCount === 1 ? "" : "s"}, ` +
          `${data.warningCount} warning${data.warningCount === 1 ? "" : "s"} left.`
        );
      }
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally {
      stopTask();
      setBusy(false);
    }
  };

  const handleFullRegenerate = () => {
    openConfirm({
      title: "Regenerate from Scratch",
      message: "A completely new post will be generated using fresh AI analysis. The current draft will be discarded.",
      confirmLabel: "♻️ Regenerate",
      confirmClass: "bg-orange-600 hover:bg-orange-700",
      onConfirm: doFullRegenerate,
    });
  };

  const doFullRegenerate = async () => {
    closeConfirm();
    setActionMsg("♻️ Regenerating from scratch…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      const result = await regeneratePost({ docId, fullRegenerate: true, taskId });
      navigate(`/blog/${result.data.newDocId}`);
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    } finally {
      stopTask();
    }
  };

  // ── Main image handlers ───────────────────────────────────────────────────
  const handleEditImage = async () => {
    if (!editPrompt.trim()) return;
    setImageModal(null);
    setActionMsg("✏️ Editing hero image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await editImageFn({ docId, editPrompt: editPrompt.trim(), imageTarget: "main", taskId });
      setMsg("🎨 Hero image updated!");
      setEditPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  const handleNewImage = async () => {
    setImageModal(null);
    setActionMsg("🖼 Generating new hero image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await regenImageFn({
        docId,
        customPrompt: newImgPrompt.trim() || undefined,
        imageTarget: "main",
        taskId,
        useDefaultPrompt: newImgUseDefault,
      });
      setMsg("🖼 New hero image ready!");
      setNewImgPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  // ── Secondary image handlers ──────────────────────────────────────────────
  const handleEditSecondaryImage = async () => {
    if (!secondaryEditPrompt.trim()) return;
    setImageModal(null);
    setActionMsg("✏️ Editing mid-article image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await editImageFn({ docId, editPrompt: secondaryEditPrompt.trim(), imageTarget: "secondary", taskId });
      setMsg("🎨 Mid-article image updated!");
      setSecondaryEditPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  const handleNewSecondaryImage = async () => {
    setImageModal(null);
    setActionMsg("🖼 Generating new mid-article image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await regenImageFn({
        docId,
        customPrompt: secondaryNewImgPrompt.trim() || undefined,
        imageTarget: "secondary",
        taskId,
        useDefaultPrompt: secondaryNewImgUseDefault,
      });
      setMsg("🖼 New mid-article image ready!");
      setSecondaryNewImgPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (!post && !notFound) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-xl text-gray-500">Loading post…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-5xl mb-4">🔍</div>
        <p className="text-gray-500 text-lg">Post not found or already published.</p>
        <button
          onClick={() => navigate("/blog")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
        >
          ← Back to list
        </button>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="relative min-h-screen bg-gray-50 -mx-4 -mt-6">
      {busy && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
          <div className="text-center px-6">
            <div className="w-14 h-14 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-5" />
            <p className="text-gray-700 text-base font-semibold mb-2">
              {actionMsg || "Working…"}
            </p>
            {taskMsg && (
              <p className="text-gray-600 text-sm mb-1">{taskMsg}</p>
            )}
            <p className="text-gray-500 text-sm">This may take a minute — please don't close this page.</p>
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          onConfirm={confirmModal.onConfirm}
          onCancel={closeConfirm}
        />
      )}

      {/* Image edit modal */}
      {imageModal === "main" && (
        <BlogImageModal
          imageUrl={post.image}
          imageLabel="Hero image"
          accentColor="purple"
          imgFormMode={imgFormMode}
          setImgFormMode={setImgFormMode}
          editPrompt={editPrompt}
          setEditPrompt={setEditPrompt}
          newImgPrompt={newImgPrompt}
          setNewImgPrompt={setNewImgPrompt}
          newImgUseDefault={newImgUseDefault}
          setNewImgUseDefault={setNewImgUseDefault}
          onEditImage={handleEditImage}
          onNewImage={handleNewImage}
          onClose={() => setImageModal(null)}
        />
      )}
      {imageModal === "secondary" && (
        <BlogImageModal
          imageUrl={post.secondaryImage}
          imageLabel="Mid-article image"
          accentColor="indigo"
          imgFormMode={secondaryImgFormMode}
          setImgFormMode={setSecondaryImgFormMode}
          editPrompt={secondaryEditPrompt}
          setEditPrompt={setSecondaryEditPrompt}
          newImgPrompt={secondaryNewImgPrompt}
          setNewImgPrompt={setSecondaryNewImgPrompt}
          newImgUseDefault={secondaryNewImgUseDefault}
          setNewImgUseDefault={setSecondaryNewImgUseDefault}
          onEditImage={handleEditSecondaryImage}
          onNewImage={handleNewSecondaryImage}
          onClose={() => setImageModal(null)}
        />
      )}

      <BlogActionBar
        onBack={() => navigate("/blog")}
        actionMsg={actionMsg}
        hasPrior={hasPrior}
        diffLoading={diffLoading}
        showDiff={showDiff}
        onToggleDiff={toggleDiff}
        showEdit={showEdit}
        onToggleEdit={() => { setShowEdit(v => !v); setShowRegenForm(false); }}
        busy={busy}
        onApprove={handleApprove}
        showRegenForm={showRegenForm}
        setShowRegenForm={setShowRegenForm}
        regenFeedback={regenFeedback}
        setRegenFeedback={setRegenFeedback}
        onRegenerate={handleRegenerate}
        onFullRegenerate={handleFullRegenerate}
        onDelete={handleDelete}
      />

      {/* Refresh drafts lead with the AI's rationale — it frames the diff. */}
      {post.isRefresh && <RefreshRationale post={post} />}

      {/* Deterministic gate results. Sits above the body so a reviewer knows
          what to look for BEFORE reading, and discloses any automatic repair. */}
      <QualityReportPanel post={post} onFix={handleFixQuality} fixing={busy} />

      {showDiff && priorPost && (
        <div ref={diffRef}>
          <BlogDiffPanel priorPost={priorPost} post={post} label={diffLabel} />
        </div>
      )}

      <BlogPostBody
        post={post}
        contentHtml={contentHtml}
        showImgPrompt={showImgPrompt}
        onToggleImgPrompt={() => setShowImgPrompt(v => !v)}
        showSecondaryImgPrompt={showSecondaryImgPrompt}
        onToggleSecondaryImgPrompt={() => setShowSecondaryImgPrompt(v => !v)}
        onOpenMainImgModal={() => setImageModal("main")}
        onOpenSecondaryImgModal={() => setImageModal("secondary")}
      />
    </div>
  );
};

export default AdminBlogPost;
